import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { classifyAuthError, getAuthErrorCode } from './classifyAuthError';
import { parseProfile } from './parseProfile';
import { ReauthRequiredError } from './ReauthRequiredError';
import { sanitizeReturnTo } from './sanitizeReturnTo';
import { AuthContext } from './useAuth';

import type { AuthProfile } from './parseProfile';
import type { AuthAppState, AuthState, AuthStatus, TokenGetter } from './useAuth';
import type { ReactNode } from 'react';

export { AUTH_CALLBACK_PATH } from './constants';

function toError(value: unknown): Error {
  return value instanceof Error ? value : new Error(String(value), { cause: value });
}

export interface AuthConfig {
  /**
   * Auth0 tenant domain, e.g. `dev-xyz.us.auth0.com`. Optional because the app's
   * env schema keeps `VITE_AUTH0_DOMAIN` optional so `task build` runs in CI
   * without it; `AuthProvider` asserts its presence at construction.
   */
  domain: string | undefined;
  /** Auth0 application (SPA) client id (`VITE_AUTH0_CLIENT_ID`). See `domain`. */
  clientId: string | undefined;
  /** Absolute URL Auth0 redirects back to after login. */
  redirectUri: string;
  /** API identifier for access tokens; fully wired by the B4 token seam. */
  audience?: string;
}

export interface AuthProviderProps {
  config: AuthConfig;
  /**
   * Called after the callback exchange completes, with the `appState` the
   * login redirect carried — the app navigates to `appState.returnTo` here.
   */
  onRedirectCallback?: (appState?: AuthAppState) => void;
  children: ReactNode;
}

function AuthStateProvider({ children }: { children: ReactNode }) {
  const {
    isLoading,
    isAuthenticated,
    error,
    getAccessTokenSilently,
    loginWithRedirect,
    logout: sdkLogout,
  } = useAuth0();
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [profile, setProfile] = useState<AuthProfile | undefined>();
  // The classified failure behind a terminal 'error' settle (fatal token
  // error or a parseProfile throw) — surfaced on AuthState.error alongside
  // the SDK's own `error`.
  const [failure, setFailure] = useState<Error | undefined>();
  // Set once by signOutLocally and never cleared: a deliberate local
  // sign-out is terminal for the document (mirrors the legacy provider).
  // A ref so the status effect below consults it synchronously after
  // sdkLogout flips isAuthenticated and re-runs it.
  const signedOutLocally = useRef(false);

  useEffect(() => {
    let cancelled = false;
    // Read through a function so post-await checks see fresh values (and
    // TS narrowing from the early return doesn't declare them impossible).
    const stale = () => cancelled || signedOutLocally.current;
    void (async () => {
      // A deliberate local sign-out outranks everything: this tab must not
      // resolve back to 'authed', and must not report 'unauthed' either —
      // that's the status the gate auto-redirects on.
      if (signedOutLocally.current) return;
      if (isLoading) {
        setStatus('loading');
        setFailure(undefined);
        return;
      }
      // The failed callback exchange is the ONLY thing that sets the SDK error;
      // terminal — surface it rather than bouncing into the failed exchange.
      if (error) {
        setProfile(undefined);
        setFailure(undefined);
        setStatus('error');
        return;
      }
      if (!isAuthenticated) {
        setProfile(undefined);
        setFailure(undefined);
        setStatus('unauthed');
        return;
      }
      // Show 'loading' BEFORE awaiting the token so a guard rendering during
      // token acquisition never sees a stale 'unauthed' and bounces a
      // freshly-logged-in user back to login (legacy lesson).
      setStatus('loading');
      let token: string;
      try {
        token = await getAccessTokenSilently();
      } catch (error_) {
        if (stale()) return;
        setProfile(undefined);
        // reauth/transient → one bounce to login; fatal → terminal (no loop
        // against a still-valid cookie).
        if (classifyAuthError(error_) === 'fatal') {
          setFailure(toError(error_));
          setStatus('error');
        } else {
          setFailure(undefined);
          setStatus('unauthed');
        }
        return;
      }
      if (stale()) return;
      try {
        setProfile(parseProfile(token));
        setFailure(undefined);
        setStatus('authed');
      } catch (error_) {
        if (stale()) return; // defensive: parseProfile is sync
        // Malformed/absent claims or an opaque token from an audience
        // mismatch — won't fix on retry; terminal. Handled entirely outside
        // classifyAuthError: a parse throw is never an SDK error and must
        // never fall into the default-transient bucket.
        setProfile(undefined);
        const unusable = toError(error_);
        // The one place the reason is visible. A zod message names the exact
        // claim path (e.g. user_metadata.company_type), which is the whole
        // diagnosis; swallowing it is what made this class of bug opaque.
        console.error('[auth] session established but profile is unusable:', unusable);
        setFailure(unusable);
        setStatus('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isLoading, isAuthenticated, error, getAccessTokenSilently]);

  const login = useCallback(
    async (options?: { returnTo?: string }) => {
      // `returnTo` is attacker-controllable (a URL query param). sanitizeReturnTo
      // reduces it to a safe same-origin path and rejects the spent /callback
      // route (returning there wedges the exchange — legacy redirectToLogin
      // lesson); the empty fallback collapses a bad value to no appState.
      const returnTo = sanitizeReturnTo(options?.returnTo, '') || undefined;
      try {
        await loginWithRedirect(returnTo ? { appState: { returnTo } } : undefined);
      } catch (error_) {
        // A deliberate local sign-out is terminal for the document, and
        // login.tsx fires login() from 'signedout' — don't overwrite it.
        if (signedOutLocally.current) return;
        // Callers fire this with `void` (useAuthGate discards the promise after
        // arming its once-guard), so a rejection would go unhandled and leave
        // the page inert. Settle terminal instead: the retry surface is the exit.
        setProfile(undefined);
        setFailure(toError(error_));
        setStatus('error');
      }
    },
    [loginWithRedirect],
  );

  const getToken = useCallback<TokenGetter>(async () => {
    // Read through a function so post-await checks see a fresh value (and
    // TS narrowing from the earlier guard doesn't declare them impossible —
    // same reasoning as `stale()` in the status effect above).
    const signedOut = () => signedOutLocally.current;
    // A deliberately signed-out tab must not resurrect a session or trip a
    // consumer's re-login (PLA-511) — throw a non-reauth error, no SDK call.
    if (signedOut()) {
      throw new Error('Auth session was signed out locally');
    }
    let token: string;
    try {
      token = await getAccessTokenSilently();
    } catch (error_) {
      // TOCTOU: signOutLocally can flip the ref while this await is in
      // flight. A local sign-out outranks whatever the SDK settled with —
      // never convert or rethrow the original error in that case.
      if (signedOut()) {
        throw new Error('Auth session was signed out locally');
      }
      if (classifyAuthError(error_) === 'reauth') {
        const code = getAuthErrorCode(error_);
        throw new ReauthRequiredError(code ?? 'login_required', { cause: error_ });
      }
      throw error_;
    }
    if (signedOut()) {
      throw new Error('Auth session was signed out locally');
    }
    return token;
  }, [getAccessTokenSilently]);

  const logout = useCallback(() => {
    void sdkLogout({
      logoutParams: { returnTo: globalThis.location.origin },
    });
  }, [sdkLogout]);

  const signOutLocally = useCallback(() => {
    signedOutLocally.current = true;
    setProfile(undefined);
    setFailure(undefined);
    setStatus('signedout');
    // Drop the SDK's in-memory session without navigating anywhere.
    void sdkLogout({ openUrl: false });
  }, [sdkLogout]);

  const combinedError = error ?? failure;
  const value = useMemo<AuthState>(
    () => ({ status, profile, error: combinedError, getToken, login, logout, signOutLocally }),
    [status, profile, combinedError, getToken, login, logout, signOutLocally],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Wraps `@auth0/auth0-react`'s provider and layers the platform auth state
 * on top (status machine + zod-parsed profile), mirroring the legacy
 * portal's `Auth0Provider → SignalAuthProvider` composition in one place.
 * Authorization Code + PKCE is the SDK's only flow, and tokens stay in
 * memory (the SDK's default `cacheLocation`) — the legacy tokens-in-memory
 * invariant. Config arrives via props so this package never reads app env.
 */
export function AuthProvider({ config, onRedirectCallback, children }: AuthProviderProps) {
  // The app's env schema keeps these optional so `task build` runs in CI with
  // none set; presence is asserted here, where Auth0 is actually constructed,
  // so a missing value fails loudly at startup naming the variable rather than
  // surfacing as an opaque SDK error at first login.
  if (!config.domain || !config.clientId) {
    const missing: string[] = [];
    if (!config.domain) missing.push('VITE_AUTH0_DOMAIN');
    if (!config.clientId) missing.push('VITE_AUTH0_CLIENT_ID');
    throw new Error(
      `Auth0 configuration is missing: ${missing.join(', ')}. Set ${
        missing.length > 1 ? 'these variables' : 'this variable'
      } for this deploy target.`,
    );
  }
  return (
    <Auth0Provider
      domain={config.domain}
      clientId={config.clientId}
      onRedirectCallback={onRedirectCallback}
      authorizationParams={{
        redirect_uri: config.redirectUri,
        ...(config.audience ? { audience: config.audience } : {}),
      }}
    >
      <AuthStateProvider>{children}</AuthStateProvider>
    </Auth0Provider>
  );
}
